import React, { useMemo, useState } from 'react';
import { CalendarIcon, UsersIcon, UserIcon, BriefcaseIcon, TrendingUpIcon, DollarSignIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, ClockIcon, BarChart3Icon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useData } from '../../contexts/DataContext';
import { formatCurrency, formatTime, getToday, getDaysInMonth, getFirstDayOfMonth, getMonthName, getStatusName, getStageName, getStageColor } from '../../utils/helpers';
interface DashboardProps {
  onNavigate: (module: string) => void;
}
export function Dashboard({
  onNavigate
}: DashboardProps) {
  const {
    patients,
    doctors,
    appointments,
    services,
    opportunities,
    contacts,
    getPatient,
    getDoctor,
    getService,
    getAppointmentsByDate,
    getTodayAppointmentsCount,
    getTotalOpportunitiesAmount
  } = useData();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getToday());
  const today = getToday();
  const todayAppointments = getAppointmentsByDate(today);
  const selectedDateAppointments = getAppointmentsByDate(selectedDate);
  // KPIs
  const totalAppointments = appointments.length;
  const todayCount = getTodayAppointmentsCount();
  const activeDoctors = doctors.length;
  const totalServices = services.length;
  const totalContacts = contacts.length;
  const totalOpportunities = opportunities.length;
  // Appointments Statistics
  const appointmentStats = useMemo(() => {
    // Last 7 days trend
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayAppointments = appointments.filter(a => a.date === dateStr);
      last7Days.push({
        date: date.toLocaleDateString('es-ES', {
          weekday: 'short',
          day: 'numeric'
        }),
        total: dayAppointments.length,
        completadas: dayAppointments.filter(a => a.status === 'completada').length,
        programadas: dayAppointments.filter(a => a.status === 'programada').length,
        canceladas: dayAppointments.filter(a => a.status === 'cancelada').length
      });
    }
    // By Doctor
    const byDoctor = appointments.reduce((acc, apt) => {
      const doctor = getDoctor(apt.doctorId);
      const doctorName = doctor?.name || 'Sin asignar';
      acc[doctorName] = (acc[doctorName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const doctorData = Object.entries(byDoctor).map(([name, count]) => ({
      name,
      citas: count
    })).sort((a, b) => b.citas - a.citas).slice(0, 5);
    return {
      last7Days,
      doctorData
    };
  }, [appointments, getDoctor]);
  // Pipeline stages
  const pipelineStages = [{
    id: 'contacto_inicial',
    name: 'Contacto Inicial'
  }, {
    id: 'evaluacion',
    name: 'Evaluación'
  }, {
    id: 'procedimiento_programado',
    name: 'Procedimiento'
  }, {
    id: 'completado',
    name: 'Completado'
  }, {
    id: 'facturacion',
    name: 'Facturación'
  }];
  const getStageCount = (stage: string) => {
    return opportunities.filter(o => o.stage === stage).length;
  };
  const getStageAmount = (stage: string) => {
    return opportunities.filter(o => o.stage === stage).reduce((sum, o) => sum + o.amount, 0);
  };
  // Calendar
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }
  const getDateString = (day: number) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };
  const getAppointmentCountForDay = (day: number) => {
    const dateStr = getDateString(day);
    return appointments.filter(a => a.date === dateStr && a.status !== 'cancelada').length;
  };
  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };
  // Recent appointments
  const recentAppointments = [...appointments].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);
  return <div className="space-y-6" data-id="element-597">
      {/* Page Header */}
      <div className="flex items-center justify-between" data-id="element-598">
        <div data-id="element-599">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-600">
            Panel de Control
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-601">
            Resumen de actividad y métricas
          </p>
        </div>
        <div className="flex gap-3" data-id="element-602">
          <Button variant="outline" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-604" />} onClick={() => onNavigate('patients')} data-id="element-603">
            Nuevo Paciente
          </Button>
          <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-606" />} onClick={() => onNavigate('appointments')} data-id="element-605">
            Nueva Cita
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-607">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('appointments')} data-id="element-608">
          <div className="flex items-center gap-3" data-id="element-609">
            <div className="p-2 bg-sky-100 dark:bg-sky-900/30 rounded-lg" data-id="element-610">
              <CalendarIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" data-id="element-611" />
            </div>
            <div data-id="element-612">
              <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-613">
                {totalAppointments}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-614">
                Citas Totales
              </p>
            </div>
          </div>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('appointments')} data-id="element-615">
          <div className="flex items-center gap-3" data-id="element-616">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg" data-id="element-617">
              <ClockIcon className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-618" />
            </div>
            <div data-id="element-619">
              <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-620">
                {todayCount}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-621">
                Citas Hoy
              </p>
            </div>
          </div>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('doctors')} data-id="element-622">
          <div className="flex items-center gap-3" data-id="element-623">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg" data-id="element-624">
              <UserIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-625" />
            </div>
            <div data-id="element-626">
              <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-627">
                {activeDoctors}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-628">
                Doctores
              </p>
            </div>
          </div>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('services')} data-id="element-629">
          <div className="flex items-center gap-3" data-id="element-630">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg" data-id="element-631">
              <BriefcaseIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-632" />
            </div>
            <div data-id="element-633">
              <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-634">
                {totalServices}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-635">
                Servicios
              </p>
            </div>
          </div>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('contacts')} data-id="element-636">
          <div className="flex items-center gap-3" data-id="element-637">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg" data-id="element-638">
              <UsersIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" data-id="element-639" />
            </div>
            <div data-id="element-640">
              <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-641">
                {totalContacts}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-642">
                Contactos
              </p>
            </div>
          </div>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigate('opportunities')} data-id="element-643">
          <div className="flex items-center gap-3" data-id="element-644">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg" data-id="element-645">
              <TrendingUpIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" data-id="element-646" />
            </div>
            <div data-id="element-647">
              <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-648">
                {totalOpportunities}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-649">
                Oportunidades
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Appointments Statistics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-650">
        {/* 7 Days Trend */}
        <Card data-id="element-651">
          <CardHeader data-id="element-652">
            <CardTitle className="flex items-center gap-2" data-id="element-653">
              <TrendingUpIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" data-id="element-654" />
              Tendencia Últimos 7 Días
            </CardTitle>
          </CardHeader>
          <CardContent data-id="element-655">
            <ResponsiveContainer width="100%" height={300} data-id="element-656">
              <LineChart data={appointmentStats.last7Days} data-id="element-657">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-658" />
                <XAxis dataKey="date" stroke="#6b7280" style={{
                fontSize: '12px'
              }} data-id="element-659" />
                <YAxis stroke="#6b7280" style={{
                fontSize: '12px'
              }} data-id="element-660" />
                <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }} data-id="element-661" />
                <Legend data-id="element-662" />
                <Line type="monotone" dataKey="completadas" stroke="#10b981" strokeWidth={2} name="Completadas" dot={{
                fill: '#10b981',
                r: 4
              }} data-id="element-663" />
                <Line type="monotone" dataKey="programadas" stroke="#3b82f6" strokeWidth={2} name="Programadas" dot={{
                fill: '#3b82f6',
                r: 4
              }} data-id="element-664" />
                <Line type="monotone" dataKey="canceladas" stroke="#ef4444" strokeWidth={2} name="Canceladas" dot={{
                fill: '#ef4444',
                r: 4
              }} data-id="element-665" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Doctors by Appointments */}
        <Card data-id="element-666">
          <CardHeader data-id="element-667">
            <CardTitle className="flex items-center gap-2" data-id="element-668">
              <UserIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" data-id="element-669" />
              Top Doctores por Citas
            </CardTitle>
          </CardHeader>
          <CardContent data-id="element-670">
            <ResponsiveContainer width="100%" height={300} data-id="element-671">
              <BarChart data={appointmentStats.doctorData} data-id="element-672">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-673" />
                <XAxis dataKey="name" stroke="#6b7280" style={{
                fontSize: '12px'
              }} data-id="element-674" />
                <YAxis stroke="#6b7280" style={{
                fontSize: '12px'
              }} data-id="element-675" />
                <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }} data-id="element-676" />
                <Bar dataKey="citas" fill="#3b82f6" radius={[8, 8, 0, 0]} name="Citas" data-id="element-677" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline */}
      <Card data-id="element-678">
        <CardHeader data-id="element-679">
          <CardTitle data-id="element-680">Pipeline de Ingresos</CardTitle>
        </CardHeader>
        <CardContent data-id="element-681">
          <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-682">
            {pipelineStages.map((stage, index) => {
            const count = getStageCount(stage.id);
            const amount = getStageAmount(stage.id);
            return <div key={stage.id} className="flex-1 min-w-[150px] p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-683">
                  <div className="flex items-center gap-2 mb-2" data-id="element-684">
                    <div className={`w-3 h-3 rounded-full ${getStageColor(stage.id)}`} data-id="element-685" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-686">
                      {stage.name}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-687">
                    {count}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-688">
                    {formatCurrency(amount)}
                  </p>
                </div>;
          })}
          </div>
        </CardContent>
      </Card>

      {/* Calendar and Appointments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-689">
        {/* Mini Calendar */}
        <Card data-id="element-690">
          <CardHeader data-id="element-691">
            <div className="flex items-center justify-between" data-id="element-692">
              <CardTitle data-id="element-693">Calendario</CardTitle>
              <div className="flex items-center gap-2" data-id="element-694">
                <button onClick={prevMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" data-id="element-695">
                  <ChevronLeftIcon className="w-5 h-5 text-gray-500" data-id="element-696" />
                </button>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[120px] text-center" data-id="element-697">
                  {getMonthName(month)} {year}
                </span>
                <button onClick={nextMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded" data-id="element-698">
                  <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-699" />
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent data-id="element-700">
            <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-701">
              {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-2" data-id="element-702">
                  {day}
                </div>)}
            </div>
            <div className="grid grid-cols-7 gap-1" data-id="element-703">
              {calendarDays.map((day, index) => {
              if (day === null) {
                return <div key={`empty-${index}`} className="aspect-square" data-id="element-704" />;
              }
              const dateStr = getDateString(day);
              const appointmentCount = getAppointmentCountForDay(day);
              const isToday = dateStr === today;
              const isSelected = dateStr === selectedDate;
              return <button key={day} onClick={() => setSelectedDate(dateStr)} className={`
                      aspect-square rounded-lg text-sm relative
                      flex flex-col items-center justify-center
                      transition-colors
                      ${isToday ? 'bg-sky-500 text-white' : ''}
                      ${isSelected && !isToday ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400' : ''}
                      ${!isToday && !isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}
                    `} data-id="element-705">
                    <span data-id="element-706">{day}</span>
                    {appointmentCount > 0 && <span className={`
                        text-[10px] font-medium
                        ${isToday ? 'text-sky-100' : 'text-sky-500 dark:text-sky-400'}
                      `} data-id="element-707">
                        {appointmentCount} cita{appointmentCount > 1 ? 's' : ''}
                      </span>}
                  </button>;
            })}
            </div>
          </CardContent>
        </Card>

        {/* Today's Appointments */}
        <Card data-id="element-708">
          <CardHeader data-id="element-709">
            <CardTitle data-id="element-710">
              Citas del {selectedDate === today ? 'Hoy' : selectedDate}
            </CardTitle>
          </CardHeader>
          <CardContent data-id="element-711">
            {selectedDateAppointments.length === 0 ? <p className="text-center text-gray-500 dark:text-gray-400 py-8" data-id="element-712">
                No hay citas programadas
              </p> : <div className="space-y-3" data-id="element-713">
                {selectedDateAppointments.map(apt => {
              const patient = getPatient(apt.patientId);
              const doctor = getDoctor(apt.doctorId);
              const service = getService(apt.serviceId);
              return <div key={apt.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-714">
                      <div className="flex items-center gap-3" data-id="element-715">
                        <div className="text-center" data-id="element-716">
                          <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-717">
                            {formatTime(apt.time)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-718">
                            {apt.duration} min
                          </p>
                        </div>
                        <div data-id="element-719">
                          <p className="font-medium text-gray-900 dark:text-white" data-id="element-720">
                            {patient?.name || 'Paciente'}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-721">
                            Dr. {doctor?.name} • {service?.name}
                          </p>
                        </div>
                      </div>
                      <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-722">
                        {getStatusName(apt.status)}
                      </Badge>
                    </div>;
            })}
              </div>}
          </CardContent>
        </Card>
      </div>

      {/* Recent Appointments */}
      <Card data-id="element-723">
        <CardHeader data-id="element-724">
          <div className="flex items-center justify-between" data-id="element-725">
            <CardTitle data-id="element-726">Citas Recientes</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => onNavigate('appointments')} data-id="element-727">
              Ver todas
            </Button>
          </div>
        </CardHeader>
        <CardContent data-id="element-728">
          <div className="overflow-x-auto" data-id="element-729">
            <table className="w-full" data-id="element-730">
              <thead data-id="element-731">
                <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-732">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-733">
                    Paciente
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-734">
                    Doctor
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-735">
                    Servicio
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-736">
                    Fecha
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-737">
                    Hora
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-738">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-739">
                {recentAppointments.map(apt => {
                const patient = getPatient(apt.patientId);
                const doctor = getDoctor(apt.doctorId);
                const service = getService(apt.serviceId);
                return <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-740">
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100" data-id="element-741">
                        {patient?.name || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100" data-id="element-742">
                        Dr. {doctor?.name || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100" data-id="element-743">
                        {service?.name || '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100" data-id="element-744">
                        {apt.date}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100" data-id="element-745">
                        {formatTime(apt.time)}
                      </td>
                      <td className="py-3 px-4" data-id="element-746">
                        <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-747">
                          {getStatusName(apt.status)}
                        </Badge>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>;
}